import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RgbaStringColorPicker } from "react-colorful";
import { colord, extend, getFormat } from "colord";
import debounce from "lodash/debounce";
import namesPlugin from "colord/plugins/names";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { Button } from "../../button/Button";
import { Select } from "../select/Select";
import { Field } from "../field/Field";
import { Input } from "../input/Input";
import { NakedButton } from "../../naked-button/NakedButton";
import { ArrowDownIcon } from "../../icons/ArrowDownIcon";
import { ArrowUpIcon } from "../../icons/ArrowUpIcon";
import { EyeDropperIcon } from "../../icons/EyeDropperIcon";
import { ColorField } from "../color-field/ColorField";
import { Popper } from "../../popper/Popper";
import { PopperBox } from "../../popper/PopperBox";
import { PortalRoot } from "../../portal-root/PortalRoot";
import { backgroundColorPalette, textColorPalette } from "./constants";
import * as Styled from "./ColorPicker.styled";
import { stringMapDefault } from "./ColorPicker.stringMap";
// Adds colord support for converting named colors like DodgerBlue to hex
extend([namesPlugin]);
const componentName = "ColorPicker";
export const ColorPicker = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { colorPalette = "background", ColorFieldStringMap, hasColorField = true, hasConfirmButtons = true, hasColorPalette = true, hasColorPaletteSelect = true, hasColorPicker = true, mode = "popover", onApply, onChange, onCancel, onClose, popperBoxProps, popperProps = {
        mode: "popover",
        placement: "top",
        shouldCloseOnOutsideInteraction: true
    }, selectedColor, stringMap: stringMapProp, trigger, ...otherProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const [activeColor, setActiveColor] = useState(selectedColor || "");
    const [activeColorPalette, setActiveColorPalette] = useState(colorPalette === "text" ? textColorPalette : colorPalette === "background" ? backgroundColorPalette : colorPalette);
    const [currentVariant, setCurrentVariant] = useState("hex");
    const lastConfirmedRef = useRef(activeColor);
    const paletteOptions = [
        { label: stringMap.colorPaletteBackground, value: "background" },
        { label: stringMap.colorPaletteText, value: "text" }
    ];
    if (Array.isArray(colorPalette)) {
        paletteOptions.push({ label: stringMap.colorPaletteCustom, value: "custom" });
    }
    useEffect(() => {
        if (!selectedColor) {
            return;
        }
        // Invalid colors sent to colord will get returned as black
        const colorValue = colord(selectedColor).isValid() ? colord(selectedColor).toHex() : selectedColor;
        setActiveColor(colorValue);
        lastConfirmedRef.current = colorValue;
        if (selectedColor && getFormat(selectedColor) === "rgb") {
            setCurrentVariant("rgba");
        }
    }, [selectedColor]);
    const handleColorPaletteSwitch = (value) => {
        if (value === "custom" && Array.isArray(colorPalette)) {
            setActiveColorPalette(colorPalette);
            return;
        }
        if (value === "text") {
            setActiveColorPalette(textColorPalette);
            return;
        }
        setActiveColorPalette(backgroundColorPalette);
    };
    const handleChange = useCallback((color) => {
        setActiveColor(color);
        onChange &&
            onChange({
                color,
                hex: colord(color).toHex(),
                rgba: colord(color).toRgbString(),
                opacity: colord(color).alpha().toString(),
                isValidCssColor: colord(color).isValid()
            });
    }, [onChange]);
    const handleChangeDebounced = useMemo(() => debounce((color) => {
        handleChange(color);
    }, 200), [handleChange]);
    const colorPicker = (closeAndReturnFocus) => {
        const handleColorChange = (color, shouldDebounce) => {
            if (shouldDebounce) {
                handleChangeDebounced(color);
            }
            else {
                handleChange(color);
            }
            if (!hasConfirmButtons && closeAndReturnFocus) {
                lastConfirmedRef.current = activeColor;
            }
        };
        return (_jsxs(Styled.ColorPickerContent, { children: [_jsxs(Styled.ColorPickerVisuallyHidden, { role: "status", "aria-live": "polite", children: [stringMap.ariaLiveActiveColor, " ", colord(activeColor).toHex()] }), hasColorPicker && (_jsxs(_Fragment, { children: [_jsx(RgbaStringColorPicker, { color: colord(activeColor).toRgbString(), onChange: (color) => {
                                handleColorChange(color, true);
                            } }), _jsxs(Styled.ColorPickerRow, { children: [_jsx(Styled.ColorPickerEyeDropper, { eyeDropperColor: activeColor, children: _jsx(Field, { label: _jsx(EyeDropperIcon, { "aria-hidden": "true" }), input: _jsx(Input, { type: "color", value: activeColor, onChange: (e) => handleColorChange(e.target.value) }) }) }), _jsx(Styled.ColorPickerSwatch, { backgroundColor: activeColor, children: colord(activeColor).toHex() === "#ffffff00" && _jsx(Styled.ColorPickerSwatchTransparentLine, {}) })] })] })), hasColorField && (_jsx(Styled.ColorPickerInputRow, { hasColorPalette: hasColorPalette, hasColorPicker: hasColorPicker, children: hasColorField && (_jsx(ColorField, { isFullWidth: true, value: colord(activeColor).isValid() ? colord(activeColor).toHex() : activeColor, defaultColor: selectedColor, label: stringMap.inputLabel, onColorChange: ({ color }) => {
                            handleColorChange(color);
                        }, stringMap: ColorFieldStringMap, shouldDisableColorPicker: true, variant: currentVariant, gutterAdornment: _jsx(Styled.ColorPickerTypeControl, { children: _jsxs(NakedButton, { onClick: () => {
                                    setCurrentVariant(currentVariant === "hex" ? "rgba" : "hex");
                                }, children: [_jsx(ArrowUpIcon, { "aria-hidden": "true" }), _jsx(ArrowDownIcon, { "aria-hidden": "true" }), _jsx(Styled.ColorPickerVisuallyHidden, { children: stringMap.inputVariantButton })] }) }) })) })), hasColorPalette && (_jsxs(Styled.ColorPickerBody, { children: [hasColorPaletteSelect && (_jsx(Styled.ColorPickerSelectWrapper, { children: _jsx(Select, { defaultValue: typeof colorPalette === "object" ? "custom" : colorPalette, options: paletteOptions, onChange: (option) => {
                                    handleColorPaletteSwitch(option.value);
                                } }) })), _jsx(Styled.ColorPickerFocusList, { role: "menu", children: activeColorPalette.map((color, index) => (_jsxs(Styled.ColorPickerColor, { role: "menuitem", backgroundColor: color, isSelected: colord(activeColor).toHex() === color, onKeyDown: (event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        handleColorChange(color);
                                    }
                                }, onClick: () => {
                                    handleColorChange(color);
                                }, children: [color === "#ffffff00" && _jsx(Styled.ColorPickerColorTransparentLine, {}), _jsx(Styled.ColorPickerVisuallyHidden, { children: color })] }, index))) })] })), hasConfirmButtons && mode === "popover" && closeAndReturnFocus && (_jsxs(Styled.ColorPickerFooter, { children: [_jsx(Button, { priority: "tertiary", size: "small", onClick: () => {
                                const previousColor = lastConfirmedRef.current;
                                const newColorObject = {
                                    color: previousColor,
                                    hex: colord(previousColor).toHex(),
                                    rgba: colord(previousColor).toRgbString(),
                                    opacity: colord(previousColor).alpha().toString(),
                                    isValidCssColor: colord(previousColor).isValid()
                                };
                                onCancel && onCancel(newColorObject);
                                onClose && onClose(newColorObject);
                                setActiveColor(previousColor);
                                closeAndReturnFocus();
                            }, children: stringMap.cancelButton }), _jsx(Button, { priority: "secondary", size: "small", onClick: () => {
                                if (!colord(activeColor).isValid()) {
                                    return;
                                }
                                lastConfirmedRef.current = activeColor;
                                onApply &&
                                    onApply({
                                        color: activeColor,
                                        hex: colord(activeColor).toHex(),
                                        rgba: colord(activeColor).toRgbString(),
                                        opacity: colord(activeColor).alpha().toString(),
                                        isValidCssColor: colord(activeColor).isValid()
                                    });
                                onClose &&
                                    onClose({
                                        color: activeColor,
                                        hex: colord(activeColor).toHex(),
                                        rgba: colord(activeColor).toRgbString(),
                                        opacity: colord(activeColor).alpha().toString(),
                                        isValidCssColor: colord(activeColor).isValid()
                                    });
                                closeAndReturnFocus();
                            }, children: stringMap.applyButton })] })), hasConfirmButtons && mode === "inline" && (_jsxs(Styled.ColorPickerFooter, { children: [_jsx(Button, { priority: "tertiary", size: "small", onClick: () => {
                                const previousColor = lastConfirmedRef.current;
                                const newColorObject = {
                                    color: previousColor,
                                    hex: colord(previousColor).toHex(),
                                    rgba: colord(previousColor).toRgbString(),
                                    opacity: colord(previousColor).alpha().toString(),
                                    isValidCssColor: colord(previousColor).isValid()
                                };
                                onCancel && onCancel(newColorObject);
                                onClose && onClose(newColorObject);
                                setActiveColor(previousColor);
                            }, children: stringMap.cancelButton }), _jsx(Button, { priority: "secondary", size: "small", onClick: () => {
                                if (!colord(activeColor).isValid()) {
                                    return;
                                }
                                lastConfirmedRef.current = activeColor;
                                onApply &&
                                    onApply({
                                        color: activeColor,
                                        hex: colord(activeColor).toHex(),
                                        rgba: colord(activeColor).toRgbString(),
                                        opacity: colord(activeColor).alpha().toString(),
                                        isValidCssColor: colord(activeColor).isValid()
                                    });
                                onClose &&
                                    onClose({
                                        color: activeColor,
                                        hex: colord(activeColor).toHex(),
                                        rgba: colord(activeColor).toRgbString(),
                                        opacity: colord(activeColor).alpha().toString(),
                                        isValidCssColor: colord(activeColor).isValid()
                                    });
                            }, children: stringMap.applyButton })] }))] }));
    };
    return (_jsx(Styled.ColorPicker, { "data-testid": "ColorPicker", ref: ref, ...otherProps, children: mode === "popover" ? (_jsx(Popper, { overlay: ({ closeAndReturnFocus }) => (_jsx(PortalRoot, { children: _jsx(PopperBox, { hasArrow: true, ...popperBoxProps, hasPadding: false, children: colorPicker(closeAndReturnFocus) }) })), trigger: trigger, ...popperProps })) : (colorPicker()) }));
});
ColorPicker.defaultProps = {
    colorPalette: "background",
    hasColorField: true
};
ColorPicker.displayName = componentName;
